import { DndContext, Modifier, useSensor, useSensors } from "@dnd-kit/core";
import {
  memo,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { CancelablePointerSensor } from "modules/cancelablePointSensor";
import { SelectedObject } from "stores/modules/app.state";
import TransformProvider from "./TransformProvider";

type Position = {
  x: number;
  y: number;
};

export type SelectedDragItem = SelectedObject & {
  data: Position;
  z: number;
  width: number;
  height: number;
  coverImageUrl?: string | null;
};

// macOS Safariでは TouchEvent が定義されておらず `x instanceof TouchEvent` と判定できないため、要素で判定する
const isTouchEvent = (x: Event | null): x is TouchEvent => {
  return x !== null && "touches" in x;
};

type DraggableContextProps = PropsWithChildren<{
  viewRef: MutableRefObject<HTMLDivElement | null>;
  centerRef: MutableRefObject<HTMLDivElement | null>;
}>;

const DraggableContext = ({
  viewRef,
  centerRef,
  children,
}: DraggableContextProps) => {
  const sensors = useSensors(
    useSensor(CancelablePointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    })
  );
  const startPosition = useRef<Position | null>(null);
  const mousePosition = useRef<Position | null>(null);

  const updateMousePosition = useCallback((e: MouseEvent | TouchEvent) => {
    const positionEvent = isTouchEvent(e) ? e.touches[0] : e;
    mousePosition.current = {
      x: positionEvent.clientX,
      y: positionEvent.clientY,
    };
  }, []);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", updateMousePosition);
      window.removeEventListener("touchmove", updateMousePosition);
    };
  }, [updateMousePosition]);

  const CustomModifier: Modifier = useCallback((args) => {
    const { active, activatorEvent, draggingNodeRect, transform } = args;
    if (!active || !draggingNodeRect) {
      startPosition.current = null;
      mousePosition.current = null;
      return transform;
    }

    const isMouse = activatorEvent instanceof MouseEvent;
    const isTouch = isTouchEvent(activatorEvent);
    if (isMouse || isTouch) {
      const event = isTouch ? activatorEvent.touches[0] : activatorEvent;
      if (!startPosition.current) {
        startPosition.current = { x: event.clientX, y: event.clientY };
      }
      if (!mousePosition.current) {
        mousePosition.current = { x: event.clientX, y: event.clientY };
      }
    }

    return {
      ...transform,
      x: (mousePosition.current?.x || 0) - (startPosition.current?.x || 0),
      y: (mousePosition.current?.y || 0) - (startPosition.current?.y || 0),
    };
  }, []);

  const handleDragStart = useCallback(() => {
    window.addEventListener("mousemove", updateMousePosition);
    window.addEventListener("touchmove", updateMousePosition);
  }, [updateMousePosition]);

  const handleDragEnd = useCallback(
    (onDragEnd: () => void) => {
      try {
        onDragEnd();
      } catch (err) {
        console.error("Error moving room objects: ", err);
      } finally {
        startPosition.current = null;
        mousePosition.current = null;
        window.removeEventListener("mousemove", updateMousePosition);
        window.removeEventListener("touchmove", updateMousePosition);
      }
    },
    [updateMousePosition]
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      modifiers={[CustomModifier]}
      autoScroll={false}
    >
      <TransformProvider
        viewRef={viewRef}
        centerRef={centerRef}
        onDragEnd={handleDragEnd}
      >
        {children}
      </TransformProvider>
    </DndContext>
  );
};

export default memo(DraggableContext);
